import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { useAuth } from '@/src/app/context/useAuth';
import { Container, Loading, SimpleButton, ErrorMessage, SimpleTextField } from '@/src/ui';
import { apiRequest } from '@/src/services';
import UsersTable from './components/UsersTable';
import CreateEditUsersModal from './components/CreateEditUsersModal';
import DeleteUsersModal from './components/DeleteUsersModal';
import UserFilters from './components/UserFilters';
import type { UserBasicDTO } from './models/userDTOs';

const UsersPage: React.FC = () => {
    document.title = 'Users';

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');

    const [searchString, setSearchString] = useState('');
    const [data, setData] = useState<Array<UserBasicDTO>>([]);
    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const FILTER_KEY = `users-filters-${userId}`;

    const [filterState, setFilterState] = useState(() => {
        try {
            const saved = localStorage.getItem(FILTER_KEY);
            if (saved) return JSON.parse(saved);
        } catch {
            // intentionally empty
        }
        return {
            userTypeMasterId: 0,
            gradeLevelMasterId: 0,
            districtId: 0,
            schoolId: 0,
            classId: 0,
        };
    });

    useEffect(() => {
        if (!userId) return;
        try {
            localStorage.setItem(FILTER_KEY, JSON.stringify(filterState));
        } catch {
            // intentionally empty
        }
    }, [filterState, userId, FILTER_KEY]);

    const [isViewingAdmin, setIsViewingAdmin] = useState(true); // Hides action columns
    const [showCreateEditModal, setShowCreateEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
    const [selectedUser, setSelectedUser] = useState<UserBasicDTO | null>(null);
    const [impersonatingUserId, setImpersonatingUserId] = useState<number | null>(null);

    const refreshTableData = useCallback(
        async (
            userTypeMasterId: number = filterState.userTypeMasterId || 1,
            gradeLevelMasterId: number = filterState.gradeLevelMasterId,
            districtId: number = filterState.districtId,
            schoolId: number = filterState.schoolId,
            classId: number = filterState.classId
        ) => {
            setError('');
            setLoaded(false);
            if (!userTypeMasterId) {
                setError('User type is required');
                setLoaded(true);
                return;
            }
            setIsViewingAdmin(userTypeMasterId === 1);

            let requestUrl = `dbo/users?userTypeMasterId=${userTypeMasterId}`;
            requestUrl += `&districtId=${districtId}`;
            requestUrl += `&schoolId=${schoolId}`;
            requestUrl += `&gradeLevelMasterId=${gradeLevelMasterId}`;
            requestUrl += `&classId=${classId}`;

            try {
                const res = await apiRequest<Array<UserBasicDTO>>('GET', requestUrl);
                setData(res.data);
            } catch {
                setError('Failed to fetch user data');
            } finally {
                setLoaded(true);
            }
        },
        [filterState]
    );

    const handleModalClose = useCallback(
        (shouldRefresh: boolean, isInTimeout: boolean) => {
            setShowCreateEditModal(false);
            setShowDeleteModal(false);
            // Don't allow the modal to be reopened while it is in its post-submit timeout
            if (!isInTimeout) {
                setSelectedUser(null);
                setModalMode(null);
            }

            if (shouldRefresh) {
                refreshTableData();
            }
        },
        [refreshTableData]
    );

    useEffect(() => {
        (async function init() {
            await refreshTableData();
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container>
            <h1 className="page-header">Users</h1>
            <br />

            <UserFilters
                refreshTableData={refreshTableData}
                setSearchString={setSearchString}
                filterState={filterState}
                setFilterState={setFilterState}
            />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <SimpleTextField
                    name="searchUsers"
                    label="Search..."
                    variant="outlined"
                    value={searchString}
                    onChange={(e) => setSearchString(e.target.value)}
                    sx={{ minWidth: 170, maxWidth: 300, mr: 2 }}
                />
                <SimpleButton
                    onClick={() => {
                        setModalMode('create');
                        setSelectedUser(null);
                        setShowCreateEditModal(true);
                    }}
                    className="whitespace-nowrap"
                    variant="contained"
                    color="primary"
                >
                    Add User
                </SimpleButton>
            </Box>

            {!loaded && <Loading message="Loading users..." />}
            <ErrorMessage error={error} />

            {loaded && (
                <UsersTable
                    data={data}
                    setShowCreateEditModal={setShowCreateEditModal}
                    setShowDeleteModal={setShowDeleteModal}
                    modalMode={modalMode}
                    setModalMode={setModalMode}
                    setSelectedUser={setSelectedUser}
                    searchString={searchString}
                    isViewingAdmin={isViewingAdmin}
                    impersonatingUserId={impersonatingUserId}
                    setImpersonatingUserId={setImpersonatingUserId}
                />
            )}

            {showCreateEditModal && (
                <CreateEditUsersModal
                    open={showCreateEditModal}
                    modalMode={modalMode}
                    resourceId={selectedUser?.id}
                    handleClose={handleModalClose}
                />
            )}

            {showDeleteModal && (
                <DeleteUsersModal
                    open={showDeleteModal}
                    resourceId={selectedUser?.id}
                    handleClose={handleModalClose}
                />
            )}
        </Container>
    );
};

export default UsersPage;
